import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { ISidebarMenu } from './sidebar-menu.interface';
import { AuthService, User } from '@app/core';

@Injectable()
export class SidebarService {
  textbookSubject: Subject<string>;
  simulateGameModalSubject: Subject<boolean>;
  rollbackGameModalSubject: Subject<boolean>;
  private loggedinUser: User;
  private readonly _mainSidebarAdminMenus: ISidebarMenu[] = [
    {
      name: 'Profile',
      path: ['/profile'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'person',
    },
    {
      name: 'Courses',
      path: ['/course/list'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'stacked_inbox',
    },
    {
      name: 'Users',
      path: ['/user'],
      ariaLabel: 'Press enter to go to the users page',
      icon: 'group',
      isDisabled: false,
    },
    {
      name: 'Heatmap',
      path: ['/heatmap'],
      ariaLabel: 'Press enter to go to the heatmap page',
      icon: 'analytics',
      isDisabled: true,
    },
  ];
  private readonly _mainSidebarInstructorMenus: ISidebarMenu[] = [
    {
      name: 'Profile',
      path: ['/profile'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'person',
    },
    {
      name: 'Courses',
      path: ['/course/list'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'stacked_inbox',
    },
    {
      name: 'Users',
      path: ['/user'],
      ariaLabel: 'Press enter to go to the users page',
      icon: 'group',
      isDisabled: false,
    },
  ];
  private readonly _mainSidebarPlayerMenus: ISidebarMenu[] = [
    {
      name: 'Profile',
      path: ['/profile'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'person',
    },
    {
      name: 'Courses',
      path: ['/course/list'],
      ariaLabel: 'Press enter to open the course list menu',
      icon: 'stacked_inbox',
    },
  ];

  private readonly _firstSidebarMenus: ISidebarMenu[];
  private readonly _secondSidebarMenus: ISidebarMenu[] = [
    {
      name: 'Summary',
      path: ['/course', 'courseId'],
      pathOptions: { exact: true },
      icon: 'data_table',
      isDisabled: false,
    },
    {
      name: 'Users',
      path: ['/course', 'courseId', 'users'],
      icon: 'group',
      haveSubMenu: false,
      isDisabled: false,
    },
    {
      path: ['/course', 'courseId', 'game'],
      icon: 'manufacturing',
      name: 'Games',
      haveSubMenu: false,
      isDisabled: false,
    },
    {
      name: 'Textbook',
      path: ['/course', 'courseId', 'textbook'],
      icon: 'menu_book',
      isDisabled: false,
    },
    {
      path: ['/dashboard', 'courseId'],
      icon: 'quiz',
      name: 'Quizzes',
      isDisabled: true,
    },
  ];

  private readonly _secondPlayerSidebarMenus: ISidebarMenu[] = [
    {
      name: 'Summary',
      path: ['/course', 'courseId'],
      pathOptions: { exact: true },
      icon: 'data_table',
      isDisabled: false,
    },
    {
      path: ['/course', 'courseId', 'game'],
      icon: 'manufacturing',
      name: 'Games',
      haveSubMenu: false,
      isDisabled: false,
    },
    {
      name: 'Textbook',
      path: ['/course', 'courseId', 'textbook'],
      icon: 'menu_book',
      isDisabled: false,
    },
    {
      path: ['/dashboard', 'courseId'],
      icon: 'quiz',
      name: 'Quizzes',
      isDisabled: true,
    },
  ];

  private readonly _gameSidebarMenus: ISidebarMenu[] = [
    {
      name: 'Summary',
      icon: 'data_table',
      path: ['/course', 'courseId', 'game', 'gameId'],
      pathOptions: { exact: true },
      isDisabled: false,
    },
    {
      name: 'Decisions',
      icon: 'rebase_edit',
      path: ['/course', 'courseId', 'game', 'gameId', 'marketId', 'entityId', 'play'],
      isDisabled: false,
    },
    {
      name: 'Simulate',
      icon: 'cycle',
      onClick: () => this.simulateGameModalSubject.next(true),
      isDisabled: false,
    },
    {
      name: 'Rollback',
      icon: 'undo',
      onClick: () => this.rollbackGameModalSubject.next(true),
      isDisabled: false,
    },
  ];

  private readonly _gamePlayerSidebarMenus: ISidebarMenu[] = [
    {
      name: 'Summary',
      icon: 'data_table',
      path: ['/course', 'courseId', 'game', 'gameId'],
      pathOptions: { exact: true },
      isDisabled: false,
    },
    {
      name: 'Decisions',
      icon: 'rebase_edit',
      path: ['/course', 'courseId', 'game', 'gameId', 'marketId', 'entityId', 'play'],
      isDisabled: false,
    },
  ];

  private readonly _helpSidebarMenus: ISidebarMenu[] = [
    {
      name: 'Quick start',
      icon: 'rocket_launch',
      path: ['/quick_start'],
      isDisabled: true,
    },
    {
      name: "Player's Manual",
      path: ['/manual'],
      ariaLabel: "Press enter to go to the usage player's manual page",
      icon: 'article',
      haveSubMenu: false,
      isDisabled: true,
    },
  ];

  constructor(private authService: AuthService) {
    this.loggedinUser = this.authService.user;
    this.textbookSubject = new Subject<string>();
    this.simulateGameModalSubject = new Subject<boolean>();
    this.rollbackGameModalSubject = new Subject<boolean>();
  }

  get mainSidebarMenus() {
    return this.loggedinUser.isAdmin
      ? this._mainSidebarAdminMenus
      : this.loggedinUser.isInstructor
        ? this._mainSidebarInstructorMenus
        : this._mainSidebarPlayerMenus;
  }

  get firstSidebarMenus() {
    return this._firstSidebarMenus;
  }

  get secondSidebarMenus() {
    return this.loggedinUser.isAdmin
      ? this._secondSidebarMenus
      : this.loggedinUser.isInstructor
        ? this._secondSidebarMenus
        : this._secondPlayerSidebarMenus;
  }

  get gameSidebarMenus() {
    return this._gameSidebarMenus;
  }

  get gamePlayerSidebarMenus() {
    return this._gamePlayerSidebarMenus;
  }

  get helpSidebarMenus() {
    return this._helpSidebarMenus;
  }
}
